import React, { useState } from "react";
import "./index.scss";

type AlertType = "info" | "success" | "warning" | "error";

interface AlertBannerProps {
  message: string;
  type?: AlertType;
  closable?: boolean;
  animated?: boolean;
}

const icons: Record<AlertType, JSX.Element> = {
  info: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="#00C48C"
      width="18"
      height="18"
    >
      <circle cx="12" cy="12" r="10" strokeWidth="2" />
      <line
        x1="12"
        y1="8"
        x2="12"
        y2="8"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <line
        x1="12"
        y1="12"
        x2="12"
        y2="16"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  ),
  success: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="#00C48C"
      width="18"
      height="18"
    >
      <path d="M20 6L9 17l-5-5" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  warning: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="#FFC107"
      width="18"
      height="18"
    >
      <path
        d="M10.29 3.86L1.82 18a1.5 1.5 0 001.29 2.25h16.78a1.5 1.5 0 001.29-2.25L13.71 3.86a1.5 1.5 0 00-2.42 0z"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <line
        x1="12"
        y1="9"
        x2="12"
        y2="13"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <line
        x1="12"
        y1="17"
        x2="12.01"
        y2="17"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  ),
  error: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="#FF4D4F"
      width="18"
      height="18"
    >
      <circle cx="12" cy="12" r="10" strokeWidth="2" />
      <line
        x1="15"
        y1="9"
        x2="9"
        y2="15"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <line
        x1="9"
        y1="9"
        x2="15"
        y2="15"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  ),
};

export const AlertBanner: React.FC<AlertBannerProps> = ({
  message,
  type = "info",
  closable = false,
  animated = true,
}) => {
  const [visible, setVisible] = useState(true);
  const [closing, setClosing] = useState(false);

  const close = () => {
    if (animated) {
      setClosing(true);
      setTimeout(() => setVisible(false), 300);
    } else {
      setVisible(false);
    }
  };

  if (!visible) return null;

  return (
    <div
      className={`alert-banner ${type} ${animated ? "animated" : ""} ${
        closing ? "closing" : "open"
      }`}
    >
      <div className="icon">{icons[type]}</div>
      <span className="message">{message}</span>
      {closable && (
        <button onClick={close} className="close-btn" aria-label="Close alert">
          ×
        </button>
      )}
    </div>
  );
};
